import React, { ChangeEvent, ChangeEventHandler, useCallback, useMemo, useState } from 'react';

import pluralize from 'pluralize';

import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { InternalRoom } from '~/services/AccommodationService';

import InternalRoomElement from './InternalRoomElement';

interface Props {
  selectedInternalRooms: Set<string>;
  internalRooms: Array<InternalRoom>;
  alreadyMappedRooms: Record<string, boolean>;
  appliedButNotSavedRooms: Array<InternalRoom>;
  handleInternalRoomSelection: (internalRoomId: string, selected: boolean) => void;
  internalPropertyRoomIds?: Set<string>;
}

export default function UnmappedRooms({
  selectedInternalRooms,
  internalRooms,
  alreadyMappedRooms,
  appliedButNotSavedRooms,
  handleInternalRoomSelection,
  internalPropertyRoomIds,
}: Props) {
  const [internalFilteringText, setInternalFilteringText] = useState('');
  const [showBedbank, setShowBedbank] = useState(true);
  const [showReservation, setShowReservation] = useState(true);

  const resetInternalFilteringText = useCallback(() => {
    setInternalFilteringText('');
  }, []);
  const handleInternalFilteringTextInput = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setInternalFilteringText(event.target.value);
  }, []);
  const handleFilterCheckboxChange = useCallback(
    (service: 'bedbank' | 'reservation') => (event: ChangeEvent<HTMLInputElement>) => {
      if (service === 'bedbank') {
        setShowBedbank(event.target.checked);
      } else if (service === 'reservation') {
        setShowReservation(event.target.checked);
      }
    },
    [],
  );

  const filteredInternalRooms = useMemo(() => {
    const normalizedFilteringText = internalFilteringText.trim().toLowerCase();

    const internalFilteredRooms = normalizedFilteringText
      ? internalRooms.filter((room) => {
          return (
            room.name.toLowerCase().includes(normalizedFilteringText) ||
            room.internalServiceId.toLowerCase().includes(normalizedFilteringText) ||
            room.internalService.toLowerCase().includes(normalizedFilteringText) ||
            room.supplierRooms.some(
              (supplierRoom) =>
                supplierRoom.name.toLowerCase().includes(normalizedFilteringText) ||
                supplierRoom.supplierId.toLowerCase().includes(normalizedFilteringText),
            )
          );
        })
      : internalRooms;

    // Rooms that have been applied but not saved should not be shown in the list
    return internalFilteredRooms
      .filter((internalRoom) => !appliedButNotSavedRooms.some((r) => r.id === internalRoom.id))
      .filter((room) => !internalPropertyRoomIds || internalPropertyRoomIds.has(room.internalServiceId))
      .filter((room) => {
        if (showBedbank && room.internalService === 'bedbank') {
          return true;
        }
        if (showReservation && room.internalService === 'reservation') {
          return true;
        }
        return false;
      });
  }, [
    appliedButNotSavedRooms,
    internalFilteringText,
    internalRooms,
    showBedbank,
    showReservation,
    internalPropertyRoomIds,
  ]);

  const selectedFromInternalRooms = useMemo(() => {
    const selectedCount = Array.from(selectedInternalRooms.entries()).length;

    const unmappedInternalRooms = internalRooms.filter((room) => !alreadyMappedRooms[room.id]);

    const totalCount = unmappedInternalRooms.length;

    return selectedCount > 0
      ? `${selectedCount} selected of ${totalCount} unmapped ${pluralize('rooms', totalCount)}`
      : `${totalCount} unmapped ${pluralize('rooms', totalCount)}`;
  }, [alreadyMappedRooms, selectedInternalRooms, internalRooms]);

  return (
    <Stack direction="column" gap={2} position="sticky" top={0} overflow="auto" height="calc(100dvh - 56px)">
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1, pt: 1, bgcolor: 'background.paper' }}>
        <Stack direction="column" gap={2}>
          <Typography variant="h6">Internal Rooms: {selectedFromInternalRooms}</Typography>
          <Stack direction="column" gap={1}>
            <TextField
              fullWidth
              label="Filter by room name:"
              value={internalFilteringText}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={resetInternalFilteringText}
                      disabled={internalFilteringText.length < 1}
                      title="clear"
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={handleInternalFilteringTextInput}
            />
            <Stack direction="row" gap={2}>
              <FormControlLabel
                control={<Checkbox checked={showBedbank} onChange={handleFilterCheckboxChange('bedbank')} />}
                label="Bedbank"
              />
              <FormControlLabel
                control={<Checkbox checked={showReservation} onChange={handleFilterCheckboxChange('reservation')} />}
                label="Reservation"
              />
            </Stack>
          </Stack>
        </Stack>
        <Divider />
      </Box>
      <Stack direction="column" gap={2}>
        {filteredInternalRooms?.map(
          (room, ind) =>
            !alreadyMappedRooms[room.internalServiceId] && (
              <InternalRoomElement
                key={`${room.internalServiceId}_${ind}`}
                room={room}
                onSelect={handleInternalRoomSelection}
                isSelected={selectedInternalRooms.has(room.id)}
                internalService={room.internalService}
              />
            ),
        )}
        {(internalFilteringText || internalPropertyRoomIds) && filteredInternalRooms.length === 0 && (
          <Typography>No rooms found based on filter</Typography>
        )}
      </Stack>
    </Stack>
  );
}
